import type { Relations, SuretyAccountId, UserId } from "@inrev/common";
import { useFetchAssistantEnabled } from "@portal/api/assistant/admin";
import {
	useFetchAdminDataNeeded,
	useFetchAdminProCons,
	useFetchDecisioningEnabled,
	useFetchProConRelationships,
} from "@portal/api/decisioning/admin";
import { LOG_DECISION_PARAM } from "@portal/components/features/decisioning";
import {
	type ToolMap,
	ToolsExpandedLayout,
	ToolsLayout,
	ToolsSidebarLayout,
} from "@portal/components/layout/ToolsLayout";
import { Button } from "@portal/components/primitives";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminActionItemTool } from "./actionItems/AdminActionItemsTool";
import { AdminActionItemIcon } from "./actionItems/components/AdminActionItemIcon";
import { AdminAssistantIcon } from "./assistant/AdminAssistantIcon";
import {
	AdminAssistantRailHeaderActions,
	AdminAssistantRailTool,
} from "./assistant/AdminAssistantTool";
import { AssistantSessionProvider, useAssistantSession } from "./assistant/useAssistantSession";
import { AdminDecisioningIcon } from "./decisioning/AdminDecisioningIcon";
import { AdminDecisioningTool } from "./decisioning/AdminDecisioningTool";
import { AdminNotesTool } from "./notes/AdminNotesTool";
import { AdminNoteIcon } from "./notes/components/AdminNoteIcon";

type AdminTool = "notes" | "actionItems" | "assistant" | "decisioning";
type AdminToolsProps = {
	relation: Relations.Notes;
	defaultUnderwriterId: UserId;
};

/** Disabled until the panel's lists load, since the sheet validates against them. */
const LogDecisionButton = ({ accountId }: { accountId: SuretyAccountId }) => {
	const [, setSearchParams] = useSearchParams();
	const { proConsIsLoading } = useFetchAdminProCons(accountId);
	const { dataNeededIsLoading } = useFetchAdminDataNeeded(accountId);
	const { relationshipsIsLoading } = useFetchProConRelationships(accountId);
	return (
		<Button
			color="primary"
			size="sm"
			disabled={proConsIsLoading || dataNeededIsLoading || relationshipsIsLoading}
			onClick={() =>
				setSearchParams((prev) => {
					const next = new URLSearchParams(prev);
					next.set(LOG_DECISION_PARAM, "open");
					return next;
				})
			}
			data-testid="decisioning-log-decision"
		>
			Log decision
		</Button>
	);
};

const isAdminTool = (value: string | null, map: ToolMap): value is AdminTool =>
	value !== null && value in map;

const buildAdminToolMap = (relation: Relations.Notes, defaultUnderwriterId: UserId): ToolMap => ({
	...(relation.relationType === "account"
		? {
				decisioning: {
					icon: AdminDecisioningIcon,
					title: "Decisioning",
					content: <AdminDecisioningTool accountId={relation.relationId} />,
					headerActions: <LogDecisionButton accountId={relation.relationId} />,
				},
			}
		: {}),
	notes: {
		icon: AdminNoteIcon,
		title: "Notes",
		content: <AdminNotesTool relation={relation} />,
	},
	actionItems: {
		icon: AdminActionItemIcon,
		title: "Action items",
		content: (
			<AdminActionItemTool relation={relation} defaultUnderwriterId={defaultUnderwriterId} />
		),
	},
	assistant: {
		icon: AdminAssistantIcon,
		title: "Assistant",
		content: <AdminAssistantRailTool relation={relation} />,
		headerActions: <AdminAssistantRailHeaderActions />,
	},
});

export const AdminTools: FC<AdminToolsProps> = ({ relation, defaultUnderwriterId }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { assistantEnabled } = useFetchAssistantEnabled();
	const { decisioningEnabled } = useFetchDecisioningEnabled();
	const assistantSession = useAssistantSession(relation);

	const toolMap = useMemo(() => {
		const next = buildAdminToolMap(relation, defaultUnderwriterId);
		if (relation.relationType === "account" || !assistantEnabled) delete next.assistant;
		if (!decisioningEnabled) delete next.decisioning;
		return next;
	}, [
		relation.relationId,
		relation.relationType,
		defaultUnderwriterId,
		assistantEnabled,
		decisioningEnabled,
	]);

	const getToolFromParams = useCallback(
		(params: URLSearchParams): AdminTool | undefined => {
			const param = params.get("adminTool");
			return isAdminTool(param, toolMap) ? param : undefined;
		},
		[toolMap],
	);

	const adminToolParam = searchParams.get("adminTool");

	const [currentTool, setCurrentToolState] = useState<AdminTool | undefined>(() =>
		getToolFromParams(searchParams),
	);

	useEffect(() => {
		setCurrentToolState(isAdminTool(adminToolParam, toolMap) ? adminToolParam : undefined);
	}, [adminToolParam, toolMap]);

	const logDecisionParam = searchParams.get(LOG_DECISION_PARAM);
	const canLogDecision = "decisioning" in toolMap;

	/** A Log decision link opens the decisioning panel, which then opens the sheet. */
	useEffect(() => {
		if (logDecisionParam === null || !canLogDecision || adminToolParam === "decisioning") return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("adminTool", "decisioning");
				return next;
			},
			{ replace: true },
		);
	}, [logDecisionParam, canLogDecision, adminToolParam, setSearchParams]);

	const setCurrentTool = (tool?: string) => {
		const candidate = tool ?? null;
		const next = isAdminTool(candidate, toolMap) ? candidate : undefined;
		setCurrentToolState(next);
		setSearchParams((prev) => {
			const nextParams = new URLSearchParams(prev);
			if (next === undefined) {
				nextParams.delete("adminTool");
			} else {
				nextParams.set("adminTool", next);
			}
			return nextParams;
		});
	};

	return (
		<AssistantSessionProvider value={assistantSession}>
			<ToolsLayout
				sidebar={
					<ToolsSidebarLayout
						currentTool={currentTool}
						toolMap={toolMap}
						setCurrentTool={setCurrentTool}
						relation={relation}
					/>
				}
				expandedSidebar={
					<ToolsExpandedLayout
						currentTool={currentTool}
						close={() => setCurrentTool(undefined)}
						setCurrentTool={setCurrentTool}
						toolMap={toolMap}
						relation={relation}
					/>
				}
			/>
		</AssistantSessionProvider>
	);
};
